'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { VStack } from '@terra/ui-styled-system';
import {
  FindingCvssVersionEditor,
  FindingSeverityReasonField,
  MetaDefault,
  ObjectEditSection,
  resolveFindingEditCvssBaseline,
} from '@terra/ui-design-system';
import {
  CVSS_SCORE_PENDING_LOCK_MS,
  FINDING_CVSS_VERSIONS,
  isFindingCvssScorePendingLocked,
  resolveFindingSelectedCvssVersion,
} from '@terra/shared-cvss';
import { FindingEditCvssSection } from './FindingEditCvssSection';
import type { FindingDetailsEditFormProps } from './findingEditForm.types';

const VERSION_FIELD = { '4.0': 'cvss40', '3.1': 'cvss31' } as const;

export function FindingEditCvssVersionsSection(
  props: FindingDetailsEditFormProps,
) {
  const t = useTranslations('designSystem.findings.cvssVersions');
  const selectedVersion = resolveFindingSelectedCvssVersion(props.finding);
  const [, setLockCheck] = useState(0);
  const lockExpiresAtMs =
    props.finding.cvssScoreStatus === 'PENDING'
      ? new Date(
          props.finding.cvssScoreRequestedAt ?? props.finding.createdAt,
        ).getTime() + CVSS_SCORE_PENDING_LOCK_MS
      : null;
  const isLocked = isFindingCvssScorePendingLocked({
    finding: props.finding,
    now: new Date(),
  });

  useEffect(() => {
    if (lockExpiresAtMs === null) {
      return;
    }

    const remainingMs = lockExpiresAtMs - Date.now();

    if (remainingMs <= 0) {
      return;
    }

    const timer = setTimeout(
      () => setLockCheck((count) => count + 1),
      remainingMs,
    );

    return () => clearTimeout(timer);
  }, [lockExpiresAtMs]);

  if (isLocked) {
    return (
      <ObjectEditSection title={t('sectionTitle')}>
        <MetaDefault color="fg.muted">{t('lockMessage')}</MetaDefault>
      </ObjectEditSection>
    );
  }

  return (
    <VStack gap={8} w="full" alignItems="stretch">
      <FindingSeverityReasonField
        severity={props.editableFields.severity}
        originalSeverity={props.originalFields?.severity}
        severityReason={props.editableFields.severityReason}
        onSeverityChange={(severity) =>
          props.handleFieldChange('severity', severity)
        }
        onSeverityReasonChange={(reason) =>
          props.handleFieldChange('severityReason', reason)
        }
      />
      {FINDING_CVSS_VERSIONS.map((version) => {
        const baseline = resolveFindingEditCvssBaseline({
          editableFields: props.editableFields,
          originalFields: props.originalFields,
          cvssVersion: props.finding.cvssVersion,
          version,
        });

        return version === selectedVersion ? (
          <FindingEditCvssSection
            key={version}
            editableFields={{ ...props.editableFields, cvss: baseline.next }}
            cvssCalculatorVersion={selectedVersion}
            findingCvssVersion={props.findingCvssVersion}
            applicationCvssEnvironmentalRequirements={
              props.applicationCvssEnvironmentalRequirements
            }
            cvssMarkableFieldProps={props.getMarkableFieldProps('cvss')}
            severityMarkableFieldProps={props.getMarkableFieldProps('severity')}
            onScoreChange={props.handleCvssScoreChange}
            originalCvss={baseline.original}
            onReasonsChange={props.onReasonsChange}
          />
        ) : (
          <FindingCvssVersionEditor
            key={version}
            version={version}
            cvss={baseline.next}
            originalCvss={baseline.original}
            onChange={(cvss) =>
              props.handleFieldChange(VERSION_FIELD[version], cvss)
            }
          />
        );
      })}
    </VStack>
  );
}
